import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';
import { todayISO } from '../lib/date';
import {
  Robot, Upload, FileText, ArrowRight, Check, Play, Plugs, MapTrifold,
  ChartBar, Lightning, ArrowsClockwise, CheckCircle, XCircle, WarningCircle,
  CaretDown, CaretRight, Spinner, ShieldCheck, Database, TreeStructure,
  Scales, ShieldWarning, GitBranch, Sparkle
} from '@phosphor-icons/react';

const StatusBadge = ({ status }) => {
  const styles = {
    passed: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
    ready: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
    good: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
    PASS: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
    warning: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
    partial: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
    WARN: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
    failed: 'text-red-400 bg-red-500/15 border-red-500/30',
    not_ready: 'text-red-400 bg-red-500/15 border-red-500/30',
    critical: 'text-red-400 bg-red-500/15 border-red-500/30',
    FAIL: 'text-red-400 bg-red-500/15 border-red-500/30',
    draft: 'text-zinc-400 bg-zinc-500/15 border-zinc-500/30',
  };
  const icons = { passed: CheckCircle, ready: CheckCircle, good: CheckCircle, PASS: CheckCircle, warning: WarningCircle, partial: WarningCircle, WARN: WarningCircle, failed: XCircle, not_ready: XCircle, critical: XCircle, FAIL: XCircle };
  const Icon = icons[status] || WarningCircle;
  return (
    <span data-testid={`badge-${status}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${styles[status] || styles.draft}`}>
      <Icon size={12} weight="fill" />{status?.replace('_', ' ')}
    </span>
  );
};

const ConfidenceRing = ({ score, size = 80 }) => {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#34d399' : score >= 50 ? '#fbbf24' : '#f87171';
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="4" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="4" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <span className="absolute text-lg font-mono font-bold" style={{ color }}>{Math.round(score)}%</span>
    </div>
  );
};

const IntegrationCopilotPage = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [blueprints, setBlueprints] = useState([]);
  const [selectedBlueprint, setSelectedBlueprint] = useState(null);
  const [parsedDoc, setParsedDoc] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [confidenceReport, setConfidenceReport] = useState(null);
  const [aiAdvisory, setAiAdvisory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [useMock] = useState(false);
  const [branches, setBranches] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [expandedStep, setExpandedStep] = useState(null);
  const [connectorStatus, setConnectorStatus] = useState([]);
  const [ingestResult, setIngestResult] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [ingestDate, setIngestDate] = useState(() => todayISO());
  const [credentialStatus, setCredentialStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);

  const fetchBlueprints = useCallback(async () => {
    try {
      const res = await api.get('/copilot/blueprints');
      setBlueprints(res.data || []);
    } catch (err) { toast.error('Failed to load blueprints'); }
  }, []);

  useEffect(() => { fetchBlueprints(); }, [fetchBlueprints]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true); setLoadingAction('Parsing documentation...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/copilot/parse-documentation', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setParsedDoc(res.data);
      toast.success(`Parsed ${res.data.source_name} — ${res.data.endpoints?.length || 0} endpoints`);
      setActiveTab('parsed');
    } catch (err) { toast.error('Failed to parse documentation'); }
    finally { setLoading(false); setLoadingAction(''); }
  };

  const handleGenerateBlueprint = async () => {
    if (!parsedDoc?.parsed_document_id) return;
    setLoading(true); setLoadingAction('Generating blueprint...');
    try {
      const res = await api.post(`/copilot/generate-blueprint?parsed_document_id=${parsedDoc.parsed_document_id}&source_name=${encodeURIComponent(parsedDoc.source_name || 'Connector')}`, {});
      setSelectedBlueprint(res.data);
      toast.success(`Blueprint generated — ${res.data.confidence_score}% confidence`);
      setActiveTab('blueprint');
      fetchBlueprints();
    } catch (err) { toast.error('Failed to generate blueprint'); }
    finally { setLoading(false); setLoadingAction(''); }
  };

  const handleTestConnection = async () => {
    setLoading(true); setLoadingAction('Testing connection...');
    try {
      const creds = { api_key: apiKey, api_secret: apiSecret };
      const res = await api.post('/copilot/rista/test-connection', creds);
      if (res.data.success) {
        toast.success(res.data.message);
        setLoadingAction('Fetching branches and catalog...');
        try {
          const [branchRes, catalogRes] = await Promise.all([
            api.post('/copilot/rista/fetch-branches', creds),
            api.post('/copilot/rista/fetch-catalog', { ...creds, branch_code: null })
          ]);
          setBranches(branchRes.data.branches || []);
          setCatalogItems(catalogRes.data.items || []);
        } catch (fetchErr) {
          const detail = fetchErr.response?.data?.detail || fetchErr.message;
          toast.error(`Connection succeeded but data fetch failed: ${detail}`);
        }
      } else {
        const stage = res.data.stage || 'unknown';
        const stageLabels = {
          auth_failure: 'Authentication failed',
          network_failure: 'Network connection failed',
          bootstrap_failure: 'Branch bootstrap failed',
        };
        toast.error(`${stageLabels[stage] || 'Connection failed'}: ${res.data.message}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Connection failed: ${detail}`);
    }
    finally { setLoading(false); setLoadingAction(''); }
  };

  const handleTestSync = async () => {
    if (!selectedBlueprint?.id) return;
    setLoading(true); setLoadingAction('Running test sync...');
    try {
      const res = await api.post(`/copilot/blueprints/${selectedBlueprint.id}/test-sync`, { api_key: apiKey, api_secret: apiSecret });
      setTestResult(res.data);
      toast.success(`Test sync: ${res.data.status} — ${res.data.confidence_score}%`);
      setActiveTab('test');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Test sync failed: ${detail}`);
    }
    finally { setLoading(false); setLoadingAction(''); }
  };

  const handleGetConfidenceReport = async () => {
    if (!selectedBlueprint?.id) return;
    setLoading(true); setLoadingAction('Generating confidence report...');
    try {
      const res = await api.post(`/copilot/blueprints/${selectedBlueprint.id}/confidence-report`, {});
      setConfidenceReport(res.data);
      setActiveTab('confidence');
    } catch (err) { toast.error('Failed to generate report'); }
    finally { setLoading(false); setLoadingAction(''); }
  };

  const handleAiAdvisory = async () => {
    if (!selectedBlueprint?.id) return;
    setLoading(true); setLoadingAction('Running AI analysis...');
    try {
      const res = await api.post(`/copilot/blueprints/${selectedBlueprint.id}/ai-advisory`, {});
      setAiAdvisory(res.data);
      toast.success('AI advisory generated');
    } catch (err) { toast.error('AI analysis failed'); }
    finally { setLoading(false); setLoadingAction(''); }
  };

  const handleConfirmMapping = async (fm) => {
    if (!selectedBlueprint?.id) return;
    try {
      const res = await api.post(`/copilot/blueprints/${selectedBlueprint.id}/update-mapping`, {}, { params: { mapping_id: fm.id, source_path: fm.source_path, target_path: fm.target_path, user_confirmed: true } });
      setSelectedBlueprint(prev => ({ ...prev, field_mappings: res.data.field_mappings || prev.field_mappings }));
      toast.success('Mapping confirmed');
    } catch (err) { toast.error('Failed to update mapping'); }
  };

  const fetchConnectorStatus = useCallback(async () => {
    try {
      const [connRes, credRes] = await Promise.all([
        api.get('/connectors/all'),
        api.get('/connectors/credentials?connector_type=rista'),
      ]);
      setConnectorStatus(connRes.data.connectors || []);
      setCredentialStatus(credRes.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchConnectorStatus(); }, [fetchConnectorStatus]);

  const handleSaveCredentials = async () => {
    if (!apiKey || !apiSecret) { toast.error('Enter API key and secret first'); return; }
    try {
      await api.post('/connectors/credentials', { api_key: apiKey, api_secret: apiSecret, connector_type: 'rista' });
      toast.success('Credentials saved securely');
      fetchConnectorStatus();
    } catch (err) { toast.error('Failed to save credentials'); }
  };

  const handleActivateConnector = async (branchCode) => {
    setLoading(true); setLoadingAction('Activating connector...');
    try {
      const res = await api.post('/connectors/activate', { branch_code: branchCode, connector_type: 'rista', sync_interval_minutes: 10 });
      toast.success(`Connector activated — syncing every ${res.data.sync_interval_minutes} minutes`);
      fetchConnectorStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally { setLoading(false); setLoadingAction(''); }
  };

  const handlePauseResume = async (branchCode, action) => {
    try {
      await api.post(`/connectors/${branchCode}/${action}`);
      toast.success(`Connector ${action}d`);
      fetchConnectorStatus();
    } catch (err) { toast.error(`Failed to ${action} connector`); }
  };

  const handleManualSync = async (branchCode) => {
    setLoading(true); setLoadingAction('Running manual sync...');
    try {
      const res = await api.post(`/connectors/${branchCode}/sync-now`);
      setIngestResult(res.data);
      toast.success(`Sync complete: ${res.data.orders_inserted || 0} inserted, ${res.data.orders_updated || 0} updated`);
      fetchConnectorStatus();
    } catch (err) { toast.error('Manual sync failed'); }
    finally { setLoading(false); setLoadingAction(''); }
  };

  const handleClearCredentials = async () => {
    if (!window.confirm('This will pause all connectors and remove saved credentials. Continue?')) return;
    try {
      await api.post('/connectors/credentials/clear', { connector_type: 'rista' });
      setCredentialStatus(null);
      setApiKey(''); setApiSecret('');
      toast.success('Credentials cleared');
      fetchConnectorStatus();
    } catch (err) { toast.error('Failed to clear credentials'); }
  };

  const fetchSyncHistory = async (branchCode) => {
    try {
      const res = await api.get(`/connectors/${branchCode}/history?limit=10`);
      setSyncHistory(res.data.runs || []);
    } catch { /* silent */ }
  };

  const handleIngestSales = async () => {
    if (!selectedBranch || !ingestDate) {
      toast.error('Select a branch and date');
      return;
    }
    setLoading(true); setLoadingAction('Starting Rista ingest...');
    let pollTimer = null;
    try {
      // POST is now an accepted-job endpoint — returns immediately with
      // job_id. No more HTTP-layer timeouts on long ingests.
      const res = await api.post('/copilot/rista/ingest-sales', {
        api_key: apiKey,
        api_secret: apiSecret,
        branch_code: selectedBranch,
        outlet_id: selectedBranch,
        start_date: ingestDate,
        end_date: ingestDate,
      });
      const jobId = res.data?.job_id;
      if (!jobId) {
        toast.error('Ingest did not return a job_id');
        setLoading(false); setLoadingAction('');
        return;
      }

      toast.info('Rista ingest running in background…');
      setLoadingAction('Ingesting sales to canonical…');

      const POLL_MS = 2500;
      const MAX_WAIT_MS = 15 * 60 * 1000; // 15 minutes ceiling
      const deadline = Date.now() + MAX_WAIT_MS;

      const pollOnce = async () => {
        if (Date.now() > deadline) {
          toast.warning('Ingest still running after 15 min — check connector status.');
          setLoading(false); setLoadingAction('');
          fetchConnectorStatus();
          return;
        }
        try {
          const jr = await api.get(`/copilot/rista/ingest-jobs/${jobId}`);
          const job = jr.data || {};
          if (job.status === 'succeeded') {
            const r = job.result || {};
            setIngestResult(r);
            fetchConnectorStatus();
            const totalOrders = (r.orders_inserted || 0) + (r.orders_updated || 0);
            toast.success(
              `Ingested ${totalOrders} orders, ${r.items_inserted || 0} items to Sales Capture`
            );
            setLoading(false); setLoadingAction('');
            return;
          }
          if (job.status === 'failed') {
            toast.error(`Ingestion failed: ${job.error || 'unknown error'}`);
            setLoading(false); setLoadingAction('');
            return;
          }
          // running — schedule next tick
          pollTimer = setTimeout(pollOnce, POLL_MS);
        } catch (err) {
          // Transient poll failure — keep polling unless auth expired
          const status = err.response?.status;
          if (status === 401) {
            toast.error('Session expired. Please re-authenticate.');
            setLoading(false); setLoadingAction('');
            return;
          }
          pollTimer = setTimeout(pollOnce, POLL_MS);
        }
      };

      pollTimer = setTimeout(pollOnce, POLL_MS);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      if (status === 401) {
        toast.error('Session expired. Please re-authenticate.');
      } else if (status === 403) {
        toast.error('Sales Capture access required for ingestion.');
      } else {
        toast.error(`Ingestion failed: ${detail}`);
      }
      if (pollTimer) clearTimeout(pollTimer);
      setLoading(false); setLoadingAction('');
    }
  };

  const groupEndpoints = (endpoints) => {
    const groups = {};
    (endpoints || []).forEach(ep => { const k = ep.purpose || 'other'; if (!groups[k]) groups[k] = []; groups[k].push(ep); });
    return groups;
  };

  const groupFieldMappings = (mappings) => {
    const groups = {};
    (mappings || []).forEach(fm => { const parts = fm.target_path?.split('.') || []; const k = parts.length >= 2 ? parts[1] : 'other'; if (!groups[k]) groups[k] = []; groups[k].push(fm); });
    return groups;
  };

  const purposeLabels = { outlet_discovery: 'Outlet Discovery', order_feed: 'Order Feed', summary_verification: 'Summary Verification', item_mapping: 'Item / Catalog', payment_data: 'Payment Data', customer_data: 'Customer Data', unknown: 'Other' };
  const guard = selectedBlueprint?.required_fields_status;

  return (
    <div className="space-y-6" data-testid="copilot-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium font-['Outfit'] tracking-tight flex items-center gap-3">
            <Robot size={28} className="text-orange-400" weight="duotone" />Integration Copilot
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Self-serve POS connector — parse, map, validate, sync</p>
        </div>
        {loading && <div className="flex items-center gap-2 text-sm text-orange-400"><Spinner size={16} className="animate-spin" />{loadingAction}</div>}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-[#121214] border border-white/5 flex-wrap">
          <TabsTrigger value="upload" data-testid="tab-upload" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400"><Upload size={16} className="mr-1.5" />Upload</TabsTrigger>
          <TabsTrigger value="parsed" data-testid="tab-parsed" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400" disabled={!parsedDoc}><FileText size={16} className="mr-1.5" />Parsed</TabsTrigger>
          <TabsTrigger value="blueprint" data-testid="tab-blueprint" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400" disabled={!selectedBlueprint}><Lightning size={16} className="mr-1.5" />Blueprint</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="tab-mapping" className="data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-400" disabled={!selectedBlueprint}><MapTrifold size={16} className="mr-1.5" />Mapping</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400" disabled={!selectedBlueprint}><Play size={16} className="mr-1.5" />Test Sync</TabsTrigger>
          <TabsTrigger value="confidence" data-testid="tab-confidence" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400" disabled={!selectedBlueprint}><ChartBar size={16} className="mr-1.5" />Confidence</TabsTrigger>
          <TabsTrigger value="ingest" data-testid="tab-ingest" className="data-[state=active]:bg-lime-500/20 data-[state=active]:text-[#D4FF00]" disabled={branches.length === 0}><Database size={16} className="mr-1.5" />Ingest</TabsTrigger>
        </TabsList>

        {/* ═══ UPLOAD TAB ═══ */}
        <TabsContent value="upload" className="space-y-4">
          <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
            <h3 className="text-base font-medium mb-4 flex items-center gap-2"><FileText size={20} className="text-orange-400" />Upload API Documentation</h3>
            <div className="border-2 border-dashed border-orange-500/30 rounded-lg p-8 text-center bg-orange-500/5 hover:bg-orange-500/10 transition-colors">
              <input type="file" accept=".json,.yaml,.yml,.pdf,.txt,.md" onChange={handleFileUpload} className="hidden" id="doc-upload" data-testid="copilot-doc-upload" />
              <label htmlFor="doc-upload" className="cursor-pointer">
                <Upload size={40} className="mx-auto text-orange-400 mb-3" weight="duotone" />
                <p className="text-sm text-zinc-300">{loading ? 'Parsing...' : 'Drop or click to upload API docs'}</p>
                <p className="text-xs text-zinc-500 mt-1">OpenAPI / Swagger JSON, YAML, Postman, or plain text</p>
              </label>
            </div>
          </div>
          <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium flex items-center gap-2"><Plugs size={20} className="text-blue-400" />Rista POS — Connect</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2"><Label className="text-xs">API Key (x-api-key)</Label><Input placeholder="Your Rista API key" value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="bg-[#18181B] border-white/10 font-mono text-sm" data-testid="rista-api-key" /></div>
              <div className="space-y-2"><Label className="text-xs">API Secret (JWT signing)</Label><Input type="password" placeholder="Your Rista API secret" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} className="bg-[#18181B] border-white/10 font-mono text-sm" data-testid="rista-api-secret" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleTestConnection} disabled={loading || (!apiKey || !apiSecret)} className="bg-blue-600 hover:bg-blue-700 text-sm" data-testid="test-rista-btn">
                {loading && loadingAction.includes('connection') ? <Spinner size={14} className="mr-2 animate-spin" /> : <Play size={14} className="mr-2" />}Test Connection
              </Button>
              {/* Save / Update — persists the typed credentials so background
                  sync, scheduled connectors, and replay scripts pick them up.
                  Without this, Test Connection only validates them in-memory. */}
              <Button onClick={handleSaveCredentials} disabled={loading || (!apiKey || !apiSecret)} className="bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="save-rista-btn">
                <ShieldCheck size={14} className="mr-2" />
                {credentialStatus?.saved ? 'Update Credentials' : 'Save Credentials'}
              </Button>
              {credentialStatus?.saved && (
                <span className="text-[11px] text-zinc-500 ml-2 font-mono">
                  Stored: {credentialStatus.api_key_masked}
                </span>
              )}
            </div>
            {branches.length > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                  <p className="text-xs font-medium text-emerald-400 mb-2 flex items-center gap-1"><Database size={14} />{branches.length} Branches</p>
                  <div className="space-y-1">{branches.map((b) => (<div key={b.external_outlet_id} className="flex items-center justify-between text-xs bg-zinc-800/40 px-2 py-1.5 rounded"><span className="font-mono text-cyan-400">{b.external_outlet_id}</span><span className="text-zinc-300">{b.external_outlet_name}</span></div>))}</div>
                </div>
                <div className="p-4 bg-violet-500/5 rounded-lg border border-violet-500/20">
                  <p className="text-xs font-medium text-violet-400 mb-2 flex items-center gap-1"><TreeStructure size={14} />{catalogItems.length} Catalog Items</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {catalogItems.slice(0, 8).map((item) => (<div key={item.external_item_name} className="flex items-center justify-between text-xs bg-zinc-800/40 px-2 py-1.5 rounded"><span className="text-zinc-300">{item.external_item_name}</span><span className="text-zinc-500">{item.category}</span></div>))}
                    {catalogItems.length > 8 && <p className="text-xs text-zinc-500 mt-1">+{catalogItems.length - 8} more</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
          {blueprints.length > 0 && (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <h3 className="text-base font-medium mb-3">Saved Blueprints</h3>
              <div className="space-y-2">
                {blueprints.map((bp) => (
                  <div key={bp.id} data-testid={`blueprint-${bp.id}`} className="flex items-center justify-between p-3 bg-zinc-800/40 rounded-lg cursor-pointer hover:bg-zinc-800/70 transition-colors border border-transparent hover:border-white/5" onClick={() => { setSelectedBlueprint(bp); setActiveTab('blueprint'); }}>
                    <div className="flex items-center gap-3">
                      <Lightning size={18} className="text-cyan-400" />
                      <div><p className="text-sm font-medium">{bp.source_name}</p><p className="text-xs text-zinc-500">{new Date(bp.created_at).toLocaleDateString()} · v{bp.mapping_version || bp.version}</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      {bp.required_fields_status && !bp.required_fields_status.sync_allowed && <span className="text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded border border-red-500/20">BLOCKED</span>}
                      <StatusBadge status={bp.readiness_status} />
                      <span className="text-sm font-mono font-medium">{Math.round(bp.confidence_score)}%</span>
                      <CaretRight size={14} className="text-zinc-500" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ═══ PARSED TAB ═══ */}
        <TabsContent value="parsed" className="space-y-4">
          {parsedDoc && (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-medium flex items-center gap-2"><FileText size={20} className="text-blue-400" />{parsedDoc.source_name}</h3>
                <ConfidenceRing score={parsedDoc.confidence_score} size={60} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500 mb-1">Auth Model</p><p className="text-sm font-medium text-white">{parsedDoc.auth?.auth_type?.replace('_', ' + ')}</p></div>
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500 mb-1">Base URL</p><p className="text-xs font-mono text-cyan-400 truncate">{parsedDoc.base_url || 'Not detected'}</p></div>
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500 mb-1">Pagination</p><p className="text-sm font-medium">{parsedDoc.pagination_type} <span className="text-zinc-500 text-xs font-mono">({parsedDoc.pagination_field})</span></p></div>
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500 mb-1">Endpoints</p><p className="text-sm font-medium">{parsedDoc.endpoints?.length || 0}</p></div>
              </div>
              {parsedDoc.auth?.jwt_config && (
                <div className="mb-5 p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
                  <p className="text-xs font-medium text-blue-400 mb-2 flex items-center gap-1"><ShieldCheck size={14} />JWT Authentication Detected</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-zinc-500">Algorithm:</span> <span className="font-mono">{parsedDoc.auth.jwt_config.algorithm}</span></div>
                    <div><span className="text-zinc-500">Header:</span> <span className="font-mono">{parsedDoc.auth.jwt_config.header_name}</span></div>
                  </div>
                </div>
              )}
              <div className="mb-5"><p className="text-xs font-medium text-zinc-400 mb-2">Endpoint Classification</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries((parsedDoc.endpoints||[]).reduce((a,e)=>{a[e.entity_type]=(a[e.entity_type]||0)+1;return a;},{})).sort((a,b)=>b[1]-a[1]).map(([type,count])=>(
                    <div key={type} className="flex items-center justify-between p-2 bg-zinc-800/30 rounded text-xs"><span className="text-zinc-400 capitalize">{type}</span><span className="font-mono font-medium text-cyan-400">{count}</span></div>
                  ))}
                </div>
              </div>
              <Button onClick={handleGenerateBlueprint} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="generate-blueprint-btn">
                {loading ? <Spinner size={14} className="mr-2 animate-spin" /> : <Lightning size={14} className="mr-2" />}Generate Connector Blueprint
              </Button>
            </div>
          )}
        </TabsContent>

        {/* ═══ BLUEPRINT TAB ═══ */}
        <TabsContent value="blueprint" className="space-y-4">
          {selectedBlueprint && (<>
            {/* Guardrails Banner */}
            {guard && !guard.sync_allowed && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4" data-testid="guardrail-block">
                <div className="flex items-center gap-2 mb-2"><ShieldWarning size={18} className="text-red-400" weight="fill" /><span className="text-sm font-medium text-red-400">Sync BLOCKED — Required fields unmapped</span></div>
                <p className="text-xs text-zinc-400 mb-2">The following fields must be mapped before any production sync:</p>
                <div className="space-y-1">{guard.unmapped?.map((u)=>(<div key={u.canonical} className="flex items-center gap-2 text-xs p-2 bg-red-500/5 rounded"><XCircle size={14} className="text-red-400" weight="fill" /><span className="text-red-300">{u.label}</span><span className="text-zinc-600 font-mono text-[10px]">{u.canonical}</span></div>))}</div>
              </div>
            )}
            {guard && guard.sync_allowed && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-2" data-testid="guardrail-pass">
                <ShieldCheck size={16} className="text-emerald-400" weight="fill" />
                <span className="text-xs text-emerald-400">All {guard.total_required} required fields mapped — sync enabled</span>
              </div>
            )}

            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-lg font-medium">{selectedBlueprint.source_name}</h3>
                  <p className="text-xs text-zinc-500 flex items-center gap-2">
                    <GitBranch size={12} />v{selectedBlueprint.mapping_version || selectedBlueprint.version}
                    {selectedBlueprint.mapping_applied_at && <span>· {new Date(selectedBlueprint.mapping_applied_at).toLocaleDateString()}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge status={selectedBlueprint.readiness_status} />
                  <ConfidenceRing score={selectedBlueprint.confidence_score} size={64} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><p className="text-xs font-medium text-zinc-400 mb-2">Connection Strategy</p>
                  <div className="p-4 bg-zinc-800/30 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-zinc-500">Base URL</span><span className="font-mono text-xs text-cyan-400">{selectedBlueprint.connection?.base_url||'—'}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Auth</span><span>{selectedBlueprint.connection?.auth_type?.replace('_',' + ')}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Sync</span><span className="capitalize">{selectedBlueprint.connection?.sync_mode}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Pagination</span><span>{selectedBlueprint.connection?.pagination_type} <span className="text-xs text-zinc-600 font-mono">({selectedBlueprint.connection?.pagination_field})</span></span></div>
                  </div>
                </div>
                <div><p className="text-xs font-medium text-zinc-400 mb-2">1OS Engine Matrix</p>
                  <div className="p-4 bg-zinc-800/30 rounded-lg space-y-2">
                    {selectedBlueprint.engine_matrix?.map((em)=>(<div key={em.engine} className="flex items-center justify-between text-sm"><span className="capitalize text-zinc-300">{em.engine}</span><span className="text-xs font-mono text-cyan-400/70">{em.contract}</span></div>))}
                  </div>
                </div>
              </div>
            </div>

            {/* Endpoint mappings */}
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <p className="text-xs font-medium text-zinc-400 mb-3">Endpoint Mappings ({selectedBlueprint.endpoints?.length||0})</p>
              <div className="space-y-3">{Object.entries(groupEndpoints(selectedBlueprint.endpoints)).slice(0,6).map(([purpose,eps])=>(
                <div key={purpose}><p className="text-xs text-zinc-500 mb-1">{purposeLabels[purpose]||purpose} ({eps.length})</p>
                  <div className="space-y-1">{eps.slice(0,5).map((ep)=>(<div key={`${ep.method}-${ep.rista_endpoint}`} className="flex items-center gap-2 text-xs bg-zinc-800/30 px-3 py-1.5 rounded"><span className={`w-11 text-center rounded px-1 py-0.5 ${ep.priority==='primary'?'bg-emerald-500/20 text-emerald-400':ep.priority==='secondary'?'bg-blue-500/15 text-blue-400':'bg-zinc-700/50 text-zinc-500'}`}>{ep.priority?.slice(0,3)}</span><span className="w-10 text-cyan-400 font-mono">{ep.method}</span><span className="flex-1 text-zinc-300 font-mono truncate">{ep.rista_endpoint}</span></div>))}{eps.length>5&&<p className="text-xs text-zinc-600 pl-2">+{eps.length-5} more</p>}</div>
                </div>
              ))}</div>
            </div>

            {/* Gaps */}
            {selectedBlueprint.gaps && (selectedBlueprint.gaps.warnings?.length>0||selectedBlueprint.gaps.risks?.length>0) && (
              <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
                <p className="text-xs font-medium text-zinc-400 mb-3">Gaps & Risks</p>
                <div className="space-y-2">
                  {selectedBlueprint.gaps.risks?.map((r,i)=>(<div key={`r-${i}`} className="flex items-start gap-2 p-2 bg-red-500/5 rounded text-xs text-red-400 border border-red-500/15"><XCircle size={14} className="mt-0.5 shrink-0" weight="fill"/>{r}</div>))}
                  {selectedBlueprint.gaps.warnings?.map((w,i)=>(<div key={`w-${i}`} className="flex items-start gap-2 p-2 bg-amber-500/5 rounded text-xs text-amber-400 border border-amber-500/15"><WarningCircle size={14} className="mt-0.5 shrink-0" weight="fill"/>{w}</div>))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setActiveTab('mapping')} variant="outline" className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10 text-sm"><MapTrifold size={14} className="mr-1.5" />Field Mapping Studio</Button>
              <Button onClick={handleTestSync} disabled={loading || (guard !== null && guard !== undefined && !guard.sync_allowed)} className="bg-emerald-600 hover:bg-emerald-700 text-sm disabled:opacity-40" data-testid="run-test-sync-btn">
                {loading&&loadingAction.includes('sync') ? <Spinner size={14} className="mr-1.5 animate-spin" /> : <Play size={14} className="mr-1.5" />}Run Test Sync
              </Button>
              <Button onClick={handleGetConfidenceReport} disabled={loading} variant="outline" className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 text-sm"><ChartBar size={14} className="mr-1.5" />Confidence Report</Button>
              <Button onClick={handleAiAdvisory} disabled={loading} variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 text-sm" data-testid="ai-advisory-btn"><Sparkle size={14} className="mr-1.5" />AI Advisory</Button>
            </div>
          </>)}
        </TabsContent>

        {/* ═══ MAPPING TAB ═══ */}
        <TabsContent value="mapping" className="space-y-4">
          {selectedBlueprint && (<>
            {/* Required fields guard */}
            {guard && (
              <div className={`rounded-lg p-4 border ${guard.sync_allowed?'bg-emerald-500/5 border-emerald-500/20':'bg-red-500/5 border-red-500/20'}`} data-testid="mapping-guard-status">
                <p className="text-xs font-medium mb-2 flex items-center gap-1.5">
                  {guard.sync_allowed ? <CheckCircle size={14} className="text-emerald-400" weight="fill" /> : <ShieldWarning size={14} className="text-red-400" weight="fill" />}
                  <span className={guard.sync_allowed?'text-emerald-400':'text-red-400'}>Required Fields: {guard.total_mapped}/{guard.total_required}</span>
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[...(guard.mapped||[]),...(guard.unmapped||[])].map((f)=>(
                    <div key={f.canonical} className={`flex items-center gap-1.5 text-xs p-1.5 rounded ${f.mapped?'bg-emerald-500/10 text-emerald-400':'bg-red-500/10 text-red-400'}`}>
                      {f.mapped ? <CheckCircle size={12} weight="fill"/> : <XCircle size={12} weight="fill"/>}{f.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-medium flex items-center gap-2"><MapTrifold size={20} className="text-violet-400" />Field Mapping Studio</h3>
                  <p className="text-xs text-zinc-500 mt-1">{selectedBlueprint.field_mappings?.length||0} auto-mapped fields · v{selectedBlueprint.mapping_version||'1.0.0'}</p>
                </div>
                <div className="text-xs text-zinc-500"><span className="text-emerald-400">{selectedBlueprint.field_mappings?.filter(f=>f.user_confirmed).length||0}</span> confirmed · <span className="text-cyan-400">{selectedBlueprint.field_mappings?.filter(f=>f.confidence>=0.8).length||0}</span> high confidence</div>
              </div>
              {Object.entries(groupFieldMappings(selectedBlueprint.field_mappings)).map(([entityType,mappings])=>(
                <div key={entityType} className="mb-5">
                  <p className="text-xs font-medium text-zinc-400 mb-2 capitalize flex items-center gap-1"><Database size={12} />{entityType} ({mappings.length} fields)</p>
                  <div className="space-y-1">{mappings.slice(0,12).map((fm,i)=>(
                    <div key={fm.id||i} className="flex items-center justify-between p-2.5 bg-zinc-800/30 rounded group hover:bg-zinc-800/50 transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xs font-mono text-cyan-400 truncate max-w-[200px]">{fm.source_path}</span>
                        <ArrowRight size={12} className="text-zinc-600 shrink-0" />
                        <span className="text-xs font-mono text-violet-400 truncate max-w-[200px]">{fm.target_path}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {fm.transformation && <span className="text-[10px] text-zinc-600 font-mono">{fm.transformation}</span>}
                        <span className={`text-xs font-mono w-10 text-right ${fm.confidence>=0.8?'text-emerald-400':fm.confidence>=0.5?'text-amber-400':'text-red-400'}`}>{(fm.confidence*100).toFixed(0)}%</span>
                        {fm.user_confirmed ? <CheckCircle size={16} className="text-emerald-400" weight="fill" /> : (
                          <button onClick={()=>handleConfirmMapping(fm)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-emerald-500/20 rounded" title="Confirm mapping"><Check size={14} className="text-emerald-400"/></button>
                        )}
                      </div>
                    </div>
                  ))}{mappings.length>12&&<p className="text-xs text-zinc-600 pl-2">+{mappings.length-12} more</p>}</div>
                </div>
              ))}
            </div>

            {/* AI Advisory Results */}
            {aiAdvisory && (
              <div className="bg-[#121214] border border-purple-500/20 rounded-lg p-6" data-testid="ai-advisory-results">
                <h3 className="text-base font-medium mb-4 flex items-center gap-2"><Sparkle size={20} className="text-purple-400" />AI Advisory <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded ml-2">ADVISORY ONLY — requires confirmation</span></h3>
                {aiAdvisory.suggestions?.length > 0 && (
                  <div className="mb-4"><p className="text-xs font-medium text-purple-400 mb-2">Suggested Mappings ({aiAdvisory.suggestions.length})</p>
                    <div className="space-y-1">{aiAdvisory.suggestions.map((s)=>(
                      <div key={`${s.source_field}-${s.suggested_target}`} className="flex items-center justify-between p-2.5 bg-purple-500/5 rounded border border-purple-500/10">
                        <div className="flex items-center gap-3 flex-1"><span className="text-xs font-mono text-cyan-400">{s.source_field}</span><ArrowRight size={12} className="text-zinc-600"/><span className="text-xs font-mono text-violet-400">{s.suggested_target}</span></div>
                        <span className="text-xs font-mono text-purple-400">{((s.confidence||0)*100).toFixed(0)}%</span>
                      </div>
                    ))}</div>
                  </div>
                )}
                {aiAdvisory.ambiguous_fields?.length > 0 && (
                  <div className="mb-4"><p className="text-xs font-medium text-amber-400 mb-2">Ambiguous Fields ({aiAdvisory.ambiguous_fields.length})</p>
                    <div className="space-y-1">{aiAdvisory.ambiguous_fields.map((f)=>(
                      <div key={f.field} className="p-2.5 bg-amber-500/5 rounded border border-amber-500/10 text-xs"><span className="font-mono text-amber-400">{f.field}</span><span className="text-zinc-500 ml-2">{f.reason}</span></div>
                    ))}</div>
                  </div>
                )}
                {aiAdvisory.improvements?.length > 0 && (
                  <div className="mb-4"><p className="text-xs font-medium text-blue-400 mb-2">Improvements</p>
                    <div className="space-y-1">{aiAdvisory.improvements.map((imp)=>(
                      <div key={imp.suggestion} className="p-2.5 bg-blue-500/5 rounded border border-blue-500/10 text-xs text-zinc-300">{imp.suggestion} <span className="text-zinc-500">— {imp.reason}</span></div>
                    ))}</div>
                  </div>
                )}
                {aiAdvisory.warnings?.length > 0 && (
                  <div>{aiAdvisory.warnings.map((w)=>(<p key={w} className="text-xs text-zinc-500">{w}</p>))}</div>
                )}
              </div>
            )}
          </>)}
        </TabsContent>

        {/* ═══ TEST SYNC TAB ═══ */}
        <TabsContent value="test" className="space-y-4">
          {testResult ? (<>
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-medium flex items-center gap-2"><Play size={20} className="text-emerald-400" />Test Sync Results</h3>
                  <p className="text-xs text-zinc-500 mt-1">{testResult.summary?.duration_ms}ms · v{testResult.mapping_version||'?'}</p>
                </div>
                <div className="flex items-center gap-3"><StatusBadge status={testResult.status} /><ConfidenceRing score={testResult.confidence_score} size={56} /></div>
              </div>
              <div className="space-y-2">{testResult.steps?.map((step)=>{
                const isExpanded = expandedStep === step.step;
                return (
                  <div key={step.step} className="bg-zinc-800/30 rounded-lg overflow-hidden border border-white/5">
                    <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-800/50 transition-colors" onClick={()=>setExpandedStep(isExpanded?null:step.step)}>
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-zinc-700/50 flex items-center justify-center text-xs font-mono">{step.step}</span>
                        <div><span className="text-sm font-medium">{step.action.replace(/_/g,' ')}</span><p className="text-xs text-zinc-500">{step.message}</p></div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-zinc-600 font-mono">{step.duration_ms}ms</span>
                        <StatusBadge status={step.status} />
                        {step.data && (isExpanded ? <CaretDown size={14} className="text-zinc-500" /> : <CaretRight size={14} className="text-zinc-500" />)}
                      </div>
                    </div>
                    {isExpanded && step.data && (<div className="px-3 pb-3"><pre className="text-xs font-mono bg-zinc-900/50 p-3 rounded overflow-x-auto max-h-48 text-zinc-400">{JSON.stringify(step.data,null,2)}</pre></div>)}
                  </div>
                );
              })}</div>
            </div>

            {/* Validation Summary */}
            {testResult.validation && (
              <div className="bg-[#121214] border border-white/5 rounded-lg p-6" data-testid="validation-summary">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-blue-400" />Pre-Ingestion Validation</h3>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="p-3 bg-zinc-800/40 rounded-lg text-center"><p className="text-2xl font-mono font-bold text-white">{testResult.validation.total_orders}</p><p className="text-xs text-zinc-500">Total</p></div>
                  <div className="p-3 bg-emerald-500/5 rounded-lg text-center border border-emerald-500/20"><p className="text-2xl font-mono font-bold text-emerald-400">{testResult.validation.passed}</p><p className="text-xs text-zinc-500">PASS</p></div>
                  <div className="p-3 bg-amber-500/5 rounded-lg text-center border border-amber-500/20"><p className="text-2xl font-mono font-bold text-amber-400">{testResult.validation.warned}</p><p className="text-xs text-zinc-500">WARN</p></div>
                  <div className="p-3 bg-red-500/5 rounded-lg text-center border border-red-500/20"><p className="text-2xl font-mono font-bold text-red-400">{testResult.validation.failed}</p><p className="text-xs text-zinc-500">FAIL</p></div>
                </div>
                {testResult.validation.issues_summary?.length > 0 && (
                  <div><p className="text-xs text-zinc-500 mb-1">Validation Rules Triggered</p>{testResult.validation.issues_summary.map((iss)=>(
                    <div key={iss.rule} className="flex items-center justify-between text-xs p-2 bg-zinc-800/30 rounded mb-1"><span className="text-zinc-400">{iss.rule.replace(/_/g,' ')}</span><span className="text-amber-400 font-mono">{iss.count}x</span></div>
                  ))}</div>
                )}
              </div>
            )}

            {/* Reconciliation */}
            {testResult.reconciliation && (
              <div className="bg-[#121214] border border-white/5 rounded-lg p-6" data-testid="reconciliation-summary">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Scales size={16} className={testResult.reconciliation.match?'text-emerald-400':'text-amber-400'} />Reconciliation Check</h3>
                <div className="flex items-center gap-3 mb-4">
                  <StatusBadge status={testResult.reconciliation.match ? 'passed' : 'warning'} />
                  <span className="text-sm font-mono">{testResult.reconciliation.variance_percent?.toFixed(1)}% max variance</span>
                </div>
                {testResult.reconciliation.variance && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs"><thead><tr className="text-zinc-500 border-b border-white/5"><th className="text-left py-2 px-3">Metric</th><th className="text-right py-2 px-3">Computed (sales/page)</th><th className="text-right py-2 px-3">Expected (summary)</th><th className="text-right py-2 px-3">Difference</th><th className="text-right py-2 px-3">Variance</th></tr></thead>
                    <tbody>{Object.entries(testResult.reconciliation.variance).map(([key,v])=>(
                      <tr key={key} className="border-b border-white/3"><td className="py-2 px-3 capitalize text-zinc-400">{key}</td><td className="py-2 px-3 text-right font-mono">{v.computed?.toLocaleString()}</td><td className="py-2 px-3 text-right font-mono">{v.expected?.toLocaleString()}</td><td className={`py-2 px-3 text-right font-mono ${v.difference>0?'text-red-400':'text-emerald-400'}`}>{v.difference>0?'+':''}{v.difference?.toLocaleString()}</td><td className={`py-2 px-3 text-right font-mono ${v.variance_percent>5?'text-red-400':v.variance_percent>1?'text-amber-400':'text-emerald-400'}`}>{v.variance_percent?.toFixed(1)}%</td></tr>
                    ))}</tbody></table>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={handleTestSync} disabled={loading} variant="outline" className="border-emerald-500/30 text-emerald-400 text-sm"><ArrowsClockwise size={14} className="mr-1.5" />Re-run</Button>
              <Button onClick={handleGetConfidenceReport} disabled={loading} className="bg-yellow-600 hover:bg-yellow-700 text-sm"><ChartBar size={14} className="mr-1.5" />Confidence Report</Button>
            </div>
          </>) : (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-12 text-center"><Play size={48} className="mx-auto text-zinc-600 mb-3" weight="duotone" /><p className="text-sm text-zinc-400">No test results yet</p><p className="text-xs text-zinc-600 mt-1">Go to Blueprint tab and run a test sync</p></div>
          )}
        </TabsContent>

        {/* ═══ CONFIDENCE TAB ═══ */}
        <TabsContent value="confidence" className="space-y-4">
          {confidenceReport ? (<>
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div><h3 className="text-base font-medium flex items-center gap-2"><ChartBar size={20} className="text-yellow-400" />Confidence Report</h3>
                  <p className="text-xs text-zinc-500 mt-1">v{confidenceReport.mapping_version||'?'}</p></div>
                <div className="text-center"><ConfidenceRing score={confidenceReport.overall_score} size={90} /><StatusBadge status={confidenceReport.status} /></div>
              </div>
              <div className="space-y-3">{confidenceReport.metrics?.map((m)=>(
                <div key={m.name} className="p-3 bg-zinc-800/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium capitalize">{m.name.replace(/_/g,' ')}</span><StatusBadge status={m.status} /></div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-zinc-700/50 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-all duration-700 ${m.status==='good'?'bg-emerald-500':m.status==='warning'?'bg-amber-500':'bg-red-500'}`} style={{width:`${(m.score/m.max_score)*100}%`}}/></div>
                    <span className="text-xs font-mono w-14 text-right text-zinc-400">{m.score}/{m.max_score}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1.5">{m.details}</p>
                </div>
              ))}</div>
            </div>
            {confidenceReport.issues?.length > 0 && (
              <div className="bg-[#121214] border border-white/5 rounded-lg p-6"><p className="text-xs font-medium text-zinc-400 mb-3">Issues ({confidenceReport.issues.length})</p>
                <div className="space-y-2">{confidenceReport.issues.map((issue)=>(
                  <div key={issue.message} className={`p-3 rounded-lg border ${issue.severity==='critical'?'bg-red-500/5 border-red-500/20':'bg-amber-500/5 border-amber-500/20'}`}><p className={`text-sm font-medium ${issue.severity==='critical'?'text-red-400':'text-amber-400'}`}>{issue.message}</p><p className="text-xs text-zinc-500 mt-1">{issue.details}</p></div>
                ))}</div>
              </div>
            )}
            {confidenceReport.recommendations?.length > 0 && (
              <div className="bg-[#121214] border border-white/5 rounded-lg p-6"><p className="text-xs font-medium text-zinc-400 mb-3">Recommendations</p>
                <div className="space-y-2">{confidenceReport.recommendations.map((r)=>(
                  <div key={r} className="flex items-start gap-2 text-sm text-zinc-300 p-2 bg-zinc-800/20 rounded"><ArrowRight size={14} className="mt-0.5 text-yellow-400 shrink-0"/>{r}</div>
                ))}</div>
              </div>
            )}
          </>) : (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-12 text-center"><ChartBar size={48} className="mx-auto text-zinc-600 mb-3" weight="duotone" /><p className="text-sm text-zinc-400">No confidence report yet</p></div>
          )}
        </TabsContent>

        {/* ═══ INGEST TAB ═══ */}
        <TabsContent value="ingest" className="space-y-4">
          {/* Credentials Status */}
          <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
            <h3 className="text-base font-medium mb-4 flex items-center gap-2"><ShieldCheck size={20} className="text-cyan-400" />Credentials</h3>
            {credentialStatus?.saved ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={18} weight="fill" className="text-emerald-400" />
                    <div>
                      <p className="text-sm text-emerald-400">Credentials saved securely</p>
                      <p className="text-xs text-zinc-500 font-mono">Key: {credentialStatus.api_key_masked} | Verified: {credentialStatus.verified_at ? new Date(credentialStatus.verified_at).toLocaleDateString() : '—'}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleClearCredentials} className="text-xs text-zinc-500 hover:text-red-400" data-testid="clear-creds-btn">Clear</Button>
                </div>
                {/* Re-save / rotate credentials in place — the Test Connection
                    flow above lets you verify a new key first; this button
                    persists it so background sync + replay scripts pick it up. */}
                {apiKey && apiSecret && (
                  <div className="p-3 bg-cyan-500/5 rounded-lg border border-cyan-500/20 flex items-center justify-between gap-3">
                    <p className="text-xs text-cyan-300">
                      You've entered new credentials. Save them to replace the
                      stored copy used by background sync and replay scripts.
                    </p>
                    <Button onClick={handleSaveCredentials} className="bg-cyan-600 hover:bg-cyan-700 text-sm whitespace-nowrap" data-testid="update-creds-btn">
                      <ShieldCheck size={14} className="mr-2" />Update Credentials
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-amber-500/5 rounded-lg border border-amber-500/20 text-xs text-amber-400">
                  No credentials saved. Enter your Rista API key and secret above, then click Save Credentials.
                </div>
                <Button onClick={handleSaveCredentials} disabled={!apiKey || !apiSecret} className="bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="save-creds-btn">
                  <ShieldCheck size={14} className="mr-2" />Save Credentials
                </Button>
              </div>
            )}
          </div>

          {/* Connector Status */}
          {connectorStatus.length > 0 && (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <h3 className="text-base font-medium mb-4 flex items-center gap-2"><Plugs size={20} className="text-[#D4FF00]" />Connectors</h3>
              <div className="space-y-3">
                {connectorStatus.map((c, i) => {
                  const statusColors = { ACTIVE: 'bg-emerald-400', PAUSED: 'bg-zinc-400', DEGRADED: 'bg-amber-400', FAILED: 'bg-red-400', TESTED: 'bg-blue-400', DRAFT: 'bg-zinc-600' };
                  const isActive = c.status === 'ACTIVE';
                  return (
                    <div key={i} className="p-4 bg-zinc-800/40 rounded-lg border border-white/5" data-testid={`connector-${c.branch_code}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-2.5 h-2.5 rounded-full ${statusColors[c.status] || 'bg-zinc-600'}`} />
                          <div>
                            <p className="text-sm font-medium">{c.connector_type} — {c.branch_code}</p>
                            <p className="text-xs text-zinc-500">{c.status} {c.sync_interval_minutes ? `• Sync every ${c.sync_interval_minutes}min` : ''} {c.credentials_saved ? '• Creds saved' : ''}</p>
                          </div>
                        </div>
                        <StatusBadge status={isActive ? 'ready' : c.status === 'FAILED' ? 'failed' : 'pending'} />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
                        <div><span className="text-zinc-500">Last sync:</span> <span className="text-zinc-300 font-mono">{c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : 'never'}</span></div>
                        <div><span className="text-zinc-500">Last date:</span> <span className="text-zinc-300 font-mono">{c.last_completed_business_date || '—'}</span></div>
                        <div><span className="text-zinc-500">Failures:</span> <span className="text-zinc-300 font-mono">{c.consecutive_failures || 0}</span></div>
                        <div><span className="text-zinc-500">Total syncs:</span> <span className="text-zinc-300 font-mono">{c.total_syncs || 0}</span></div>
                      </div>
                      {c.last_error && <p className="text-xs text-red-400/70 mb-3 font-mono truncate">{c.last_error}</p>}
                      <div className="flex gap-2">
                        {isActive ? (
                          <Button variant="outline" size="sm" onClick={() => handlePauseResume(c.branch_code, 'pause')} className="text-xs" data-testid={`pause-${c.branch_code}`}>Pause</Button>
                        ) : (c.status === 'PAUSED' || c.status === 'DEGRADED') ? (
                          <Button variant="outline" size="sm" onClick={() => handlePauseResume(c.branch_code, 'resume')} className="text-xs" data-testid={`resume-${c.branch_code}`}>Resume</Button>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={() => handleManualSync(c.branch_code)} disabled={loading} className="text-xs" data-testid={`sync-now-${c.branch_code}`}>
                          <ArrowsClockwise size={12} className="mr-1" />Sync Now
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => fetchSyncHistory(c.branch_code)} className="text-xs text-zinc-500" data-testid={`history-${c.branch_code}`}>History</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sync History */}
          {syncHistory.length > 0 && (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
              <h3 className="text-base font-medium mb-4">Sync History</h3>
              <div className="space-y-2">
                {syncHistory.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-zinc-800/30 rounded-lg text-xs">
                    <div className="flex items-center gap-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.trigger === 'scheduled' ? 'bg-blue-500/10 text-blue-400' : r.trigger === 'manual' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-zinc-500/10 text-zinc-400'}`}>{r.trigger || 'manual'}</span>
                      <span className="font-mono text-zinc-300">{r.business_date}</span>
                    </div>
                    <div className="flex items-center gap-4 text-zinc-400">
                      <span>+{r.orders_inserted || 0} / ~{r.orders_updated || 0}</span>
                      <span>{r.items_inserted || 0} items</span>
                      <span className="text-zinc-500">{r.created_at ? new Date(r.created_at).toLocaleTimeString() : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ingest Controls */}
          <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
            <h3 className="text-base font-medium mb-4 flex items-center gap-2"><ArrowsClockwise size={20} className="text-[#D4FF00]" />Fetch & Ingest Sales</h3>
            {branches.length === 0 ? (
              <div className="p-8 text-center bg-zinc-800/20 rounded-lg">
                <p className="text-sm text-zinc-400">Connect to Rista first (Upload tab) to discover branches.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Branch</Label>
                    <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)}
                      className="w-full bg-[#18181B] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="ingest-branch-select">
                      <option value="">Select branch...</option>
                      {branches.map(b => (
                        <option key={b.external_outlet_id} value={b.external_outlet_id}>
                          {b.external_outlet_name} ({b.external_outlet_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Business Date</Label>
                    <Input type="date" value={ingestDate} onChange={e => setIngestDate(e.target.value)}
                      className="bg-[#18181B] border-white/10 text-sm" data-testid="ingest-date-input" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleIngestSales} disabled={loading || !selectedBranch || !ingestDate}
                      className="bg-[#D4FF00] hover:bg-[#c4ef00] text-black font-medium w-full" data-testid="ingest-sales-btn">
                      {loading && loadingAction.includes('Ingesting') ? <Spinner size={14} className="mr-2 animate-spin" /> : <ArrowsClockwise size={14} className="mr-2" />}
                      Ingest to Sales Capture
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-zinc-500">This will fetch sales from Rista, normalize them, and write directly to canonical Sales Capture collections. Data will appear in Sales Explorer.</p>
              </div>
            )}
          </div>

          {/* Ingest Result */}
          {ingestResult && (
            <div className={`bg-[#121214] border rounded-lg p-6 ${ingestResult.success ? 'border-emerald-500/20' : 'border-red-500/20'}`}>
              <h3 className="text-base font-medium mb-4 flex items-center gap-2">
                {ingestResult.success ? <CheckCircle size={20} className="text-emerald-400" weight="fill" /> : <XCircle size={20} className="text-red-400" weight="fill" />}
                Ingestion Result
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500">Fetched</p><p className="text-lg font-mono text-white">{ingestResult.sales_fetched}</p></div>
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500">Inserted</p><p className="text-lg font-mono text-emerald-400">{ingestResult.orders_inserted}</p></div>
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500">Updated</p><p className="text-lg font-mono text-blue-400">{ingestResult.orders_updated}</p></div>
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500">Items</p><p className="text-lg font-mono text-cyan-400">{ingestResult.items_inserted}</p></div>
                <div className="p-3 bg-zinc-800/40 rounded-lg"><p className="text-xs text-zinc-500">Pages</p><p className="text-lg font-mono text-zinc-300">{ingestResult.pages_fetched}</p></div>
              </div>
              {ingestResult.reconciliation && (
                <div className={`p-3 rounded-lg border text-xs ${ingestResult.reconciliation.match ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' : 'bg-amber-500/5 border-amber-500/20 text-amber-400'}`}>
                  <p className="font-medium mb-1">Reconciliation: {ingestResult.reconciliation.match ? 'PASS' : 'VARIANCE'}</p>
                  <p>Computed net: {ingestResult.reconciliation.computed_net?.toFixed(2)} | Expected: {ingestResult.reconciliation.expected_net?.toFixed(2)} | Variance: {ingestResult.reconciliation.variance_percent}%</p>
                </div>
              )}
              {ingestResult.errors?.length > 0 && (
                <div className="mt-3 p-3 bg-red-500/5 rounded-lg border border-red-500/20 text-xs text-red-400">
                  <p className="font-medium mb-1">Errors ({ingestResult.errors.length})</p>
                  {ingestResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              {ingestResult.success && credentialStatus?.saved && selectedBranch && !connectorStatus.find(c => c.branch_code === selectedBranch && c.status === 'ACTIVE') && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <Button onClick={() => handleActivateConnector(selectedBranch)} disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-sm w-full" data-testid="activate-connector-btn">
                    <Plugs size={14} className="mr-2" />Activate Connector — Auto-sync every 10 minutes
                  </Button>
                  <p className="text-[10px] text-zinc-500 mt-2 text-center">Saves credentials and starts scheduled background sync. You can pause anytime.</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegrationCopilotPage;
